import React from "react";

/**
 * Top-level React ErrorBoundary.
 *
 * Catches any unhandled render-time error (incl. lazy-chunk load failures,
 * stale cached bundles, third-party widget crashes) and shows a friendly
 * fallback UI instead of a blank white page.
 *
 * Provides 3 escape hatches for the user:
 *   1. Soft retry: clear state and try rendering children again
 *   2. Hard reload: force-refresh the page bypassing cache
 *   3. Full cleanup: clear caches + service workers + localStorage flags
 *      then reload (helps users stuck on a corrupted cached bundle)
 *
 * Also logs the error to the console (caught by Sentry/LogRocket if wired
 * later) and to a small in-memory ring so support can ask the customer to
 * read the last error.
 */
export class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error("[AppErrorBoundary]", error, errorInfo);
    this.setState({ errorInfo });
    // Best-effort: notify a future Sentry/observability backend.
    try {
      window.__lastAppError = {
        message: error?.message || String(error),
        stack: error?.stack || "",
        componentStack: errorInfo?.componentStack || "",
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      };
    } catch (_) { /* no-op */ }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleHardReload = async () => {
    // ALWAYS purge caches + ServiceWorker first, then reload with cache-bust
    // querystring. This is the most common fix when a customer is stuck on a
    // corrupted/stale bundle: the previous version (split into 2 buttons) was
    // confusing — many users clicked the orange "Reload" button repeatedly,
    // never trying the "Cleanup" option, and stayed stuck. Now there's one
    // big button that does both.
    // Always remove only the local cart first. This must never depend on a
    // Cache API or ServiceWorker promise resolving successfully.
    try { localStorage.removeItem("cart"); } catch (_) { /* no-op */ }
    const cleanupTasks = [];
    try {
      if ("caches" in window) {
        cleanupTasks.push(caches.keys()
          .then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
          .catch(() => undefined));
      }
      if ("serviceWorker" in navigator) {
        cleanupTasks.push(navigator.serviceWorker.getRegistrations()
          .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
          .catch(() => undefined));
      }
    } catch (_) { /* reload continues even when browser cleanup is unavailable */ }
    const scheduleTimeout = typeof window.setTimeout === "function"
      ? window.setTimeout.bind(window)
      : (typeof globalThis.setTimeout === "function" ? globalThis.setTimeout.bind(globalThis) : null);
    const timeout = new Promise((resolve) => {
      if (scheduleTimeout) scheduleTimeout(resolve, 2000);
      else resolve();
    });
    await Promise.race([Promise.all(cleanupTasks), timeout]);
    // ?_t=timestamp forces the browser to skip the HTML cache and re-fetch
    // (which in turn re-fetches the latest /static/js/main.<hash>.js).
    const url = new URL(window.location.href);
    url.searchParams.set("_t", Date.now().toString());
    // Use location.replace so the broken page isn't kept in history (back
    // button would just send the user back to the broken state).
    window.location.replace(url.toString());
  };

  // Kept for backward compat (and as an explicit "nuclear" option).
  handleFullCleanup = async () => {
    return this.handleHardReload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const errMsg = this.state.error?.message || "Errore sconosciuto";

    return (
      <div
        data-testid="app-error-boundary"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "linear-gradient(180deg, #0a0a0f 0%, #1a1a24 100%)",
          color: "#e5e7eb",
          fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: 480,
            width: "100%",
            background: "#12121a",
            border: "1px solid #2a2a3a",
            borderRadius: 12,
            padding: "32px 24px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.4)",
          }}
        >
          <div style={{ fontSize: 48, textAlign: "center", marginBottom: 16 }}>⚠️</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12, textAlign: "center" }}>
            Oops, c'è stato un errore di caricamento
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.5, color: "#9ca3af", marginBottom: 20, textAlign: "center" }}>
            Sembra che il sito non si stia caricando correttamente.
            Nella maggior parte dei casi si risolve ricaricando la pagina.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button
              type="button"
              onClick={this.handleHardReload}
              data-testid="error-reload-btn"
              style={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                color: "#0a0a0f",
                border: "none",
                borderRadius: 8,
                padding: "14px 16px",
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              🔄 Pulisci cache e ricarica
            </button>
            <a
              href="https://wa.me/393470808245"
              style={{
                textAlign: "center",
                color: "#60a5fa",
                fontSize: 12,
                textDecoration: "none",
                marginTop: 6,
              }}
            >
              Il problema continua? Scrivici su WhatsApp
            </a>
          </div>

          <details style={{ marginTop: 20, fontSize: 11, color: "#6b7280" }}>
            <summary style={{ cursor: "pointer", userSelect: "none" }}>
              Dettagli tecnici (per il supporto)
            </summary>
            <pre
              style={{
                marginTop: 8,
                padding: 10,
                background: "#0a0a0f",
                border: "1px solid #2a2a3a",
                borderRadius: 6,
                overflow: "auto",
                maxHeight: 140,
                fontSize: 10,
                lineHeight: 1.4,
                color: "#9ca3af",
              }}
            >
              {errMsg}
              {"\n\n"}
              {this.state.error?.stack?.split("\n").slice(0, 5).join("\n")}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
